import { useState } from 'react';
import { Flex, Button, Box, Loader, Icon, Text, ProgressBar, Checkbox } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Heading } from '@vibe/core';
import { monday } from '../services/monday';
import { ViewerBoardInfo, ViewerBoardItem, Settings, ViewerAsset } from '@gorilla/widgets-viewer/app/viewer/types';
import { removeUnusedAssets } from '@gorilla/widgets-shared/src/services/publish';
import { ShareBox } from './ShareBox';
import { Alert, Completed, File, DropdownChevronRight } from '@vibe/icons';
import { usePublish } from '../hooks/use-publish';

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

type PublishModalProps = {
  show: boolean;
  boardInfo: ViewerBoardInfo;
  items: ViewerBoardItem[];
  assets: ViewerAsset[];
  settings: Settings;
  isPublished: boolean;
  widgetInfo: any;
  path: string;
  onClose: () => void;
};

export function PublishModal({ show, onClose, boardInfo, items, assets, settings, path, isPublished, widgetInfo }: PublishModalProps) {
  const [agreeTOS, setAgreeTOS] = useState<boolean | false>(false);
  const [showFiles, setShowFiles] = useState<boolean | false>(false);
  const [showLog, setShowLog] = useState<boolean | false>(false);
  const { id, publish, progress, errorMessage, log } = usePublish();
  const usedAssets = removeUnusedAssets(assets, boardInfo, items, settings) as ViewerAsset[];

  let status = 'init';

  if (errorMessage) {
    status = 'error';
  } else if (id !== null) {
    status = 'published';
  } else if (progress !== null) {
    status = 'publishing';
  }

  let buttonText = 'Publish widget';

  if (status === 'published') {
    buttonText = 'Published';
  } else if (status === 'publishing') {
    buttonText = 'Publish widget';
  }

  let filesList = null;
  let logList = null;

  const hasWarnings = (log || []).some((entry) => entry.type === 'warning');

  if (showFiles) {
    filesList = (
      <Box marginTop="small">
        <Box
          //padding="large"
          //paddingTop="medium"
          border
          borderColor="layoutBorderColor"
          rounded="small"
          backgroundColor="greyBackgroundColor"
        >
          <div style={{ overflowY: 'auto', padding: '16px', width: '485px', height: '60px' }}>
            {usedAssets.map((asset) => {
              return (
                <div key={asset.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                  <Icon icon={File} />{' '}
                  <div style={{ overflow: 'hidden', maxWidth: '350px', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{asset.name}</div>{' '}
                  <span style={{ color: '#afb0bd' }}>({formatBytes(asset.size)})</span>
                </div>
              );
            })}
          </div>
        </Box>
      </Box>
    );
  }

  if (showLog && (status === 'published' || status === 'error')) {
    logList = (
      <Box marginTop="small">
        <Box
          //padding="large"
          //paddingTop="medium"
          border
          borderColor="layoutBorderColor"
          rounded="small"
          backgroundColor="greyBackgroundColor"
        >
          <div style={{ overflowY: 'auto', padding: '16px', width: '485px', height: '100px' }}>
            {log.map((entry) => {
              return (
                <div key={entry.message} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                  <Icon icon={DropdownChevronRight} /> {entry.message}
                  {entry.type === 'error' ? <span style={{ color: '#e73a3a' }}> (Error)</span> : null}
                  {entry.type === 'warning' ? <span style={{ color: 'orange' }}> (Warning)</span> : null}
                </div>
              );
            })}
          </div>
        </Box>
      </Box>
    );
  }

  const assetsTotalSize = usedAssets.reduce((acc, asset) => {
    return acc + asset.size;
  }, 0);

  return (
    <AppModal
      contentSpacing={false}
      alertDialog={true}
      data-testid="publishModal"
      width="default"
      bodyStyle={{ paddingBottom: 32 }}
      show={show}
      onClose={() => {
        if (status !== 'publishing') {
          onClose();
        }
      }}
    >
      <AppModalContent>
        <Box marginBottom="large">
          <Heading type="h3" weight="bold" align="start" style={{ marginBottom: '8px' }}>
            Publish widget
          </Heading>
          <Flex gap={8} direction="column" align="start">
            <Box>
              <Text type="text2" align="start" color="primary" element="p">
                The widget will appear as is after publishing, including all board items and related files from the boards current state. It
                remains published until it is unpublished, deleted or until 5 days after the app was uninstalled. Changes made after
                publishing, need to be republished to take effect. Or use an automation to publish the widget, by that you automatically
                agree to the terms below.
              </Text>
            </Box>

            {status === 'init' ? (
              <>
                <Box marginTop="small">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                    <Checkbox
                      checked={agreeTOS}
                      label=" "
                      onChange={() => {
                        {
                          agreeTOS === false ? setAgreeTOS(true) : setAgreeTOS(false);
                        }
                      }}
                    />
                    <small style={{ marginTop: '0px', width: '470px', fontSize: '0.6em', lineHeight: '1.5em', color: '#676879' }}>
                      I accept the{' '}
                      <a
                        href="https://getgorilla.app/privacy/"
                        target="_blank"
                        style={{ fontSize: 'inherit', lineHeight: 'inherit', textDecoration: 'none' }}
                      >
                        Privacy Policy
                      </a>
                      , the{' '}
                      <a
                        href="https://getgorilla.app/terms/"
                        target="_blank"
                        style={{ fontSize: 'inherit', lineHeight: 'inherit', textDecoration: 'none' }}
                      >
                        Terms of Service
                      </a>
                      , that all board items{' '}
                      {usedAssets.length ? (
                        <>
                          {' '}
                          including{' '}
                          <a
                            href=""
                            onClick={(ev) => {
                              ev.preventDefault();
                              setShowFiles(!showFiles);
                            }}
                            style={{ fontSize: 'inherit', lineHeight: 'inherit', textDecoration: 'none' }}
                          >
                            Files
                          </a>{' '}
                          ({formatBytes(assetsTotalSize)})&nbsp;
                        </>
                      ) : null}
                      are processed and stored outside of monday.com, and that published data might be indexed by search engines or AI bots.
                    </small>
                  </div>
                </Box>
                {filesList}
              </>
            ) : null}
          </Flex>
        </Box>

        {/* PUBLISHING */}

        {status !== 'init' ? (
          <Box marginBottom="small">
            <ProgressBar value={progress} size="large" barStyle={status === 'published' ? 'positive' : 'primary'} />
          </Box>
        ) : (
          <></>
        )}

        {status !== 'init' ? (
          <Box marginBottom="medium">
            <Flex gap={7}>
              {status === 'publishing' ? (
                <>
                  <Loader size={14} color="primary" />
                  <small style={{ display: 'block', lineHeight: '20px', color: '#0073ea' }}>Publishing widget</small>
                </>
              ) : null}
              {status === 'published' ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#00854d' }}>
                    <Icon icon={Completed} />
                  </div>
                  <small style={{ display: 'block', lineHeight: '20px', color: '#00854d' }}>
                    Widget published {hasWarnings ? <span style={{ color: 'orange' }}> (with warnings)</span> : null}
                  </small>{' '}
                  <a
                    href=""
                    onClick={(ev) => {
                      ev.preventDefault();
                      setShowLog(!showLog);
                    }}
                    style={{ marginLeft: 'auto', fontSize: 'smaller', textDecoration: 'none' }}
                  >
                    {showLog ? 'Hide' : 'Show'} details
                  </a>
                </>
              ) : null}
              {status === 'error' ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#e73a3a' }}>
                    <Icon icon={Alert} />
                  </div>
                  <small style={{ display: 'block', lineHeight: '20px', color: '#e73a3a' }}>{errorMessage}</small> <br />
                  <a
                    href=""
                    onClick={(ev) => {
                      ev.preventDefault();
                      setShowLog(!showLog);
                    }}
                    style={{ marginLeft: 'auto' }}
                  >
                    {showLog ? 'Hide' : 'Show'} details
                  </a>
                </>
              ) : null}
            </Flex>
            <>{logList}</>
          </Box>
        ) : (
          <></>
        )}

        {!id ? (
          <Box>
            <Flex gap={16}>
              <Button
                disabled={agreeTOS === false || progress !== null}
                size="medium"
                kind="primary"
                onClick={async () => {
                  monday.execute('valueCreatedForUser');
                  await publish(
                    settings,
                    boardInfo,
                    items,
                    usedAssets.map((asset) => asset.id),
                    path,
                  );
                }}
                style={{
                  width: '100%',
                }}
              >
                {buttonText}
              </Button>
            </Flex>
          </Box>
        ) : (
          <></>
        )}

        {(isPublished || id) && status === 'published' ? (
          <ShareBox
            widgetId={widgetInfo.id || id}
            introText={
              id ? (
                <Box marginBottom="medium">
                  <h4 style={{ marginBottom: '8px' }}>Congratulations, your widget has been published.</h4>
                  <Text type="text2" align="start" color="primary" element="p">
                    If changes are made to the board now, the widget must be republished that changes take effect. The share link and embed
                    code are always preserved. You can also use an automation to publish the widget.
                  </Text>
                </Box>
              ) : null
            }
          />
        ) : (
          <></>
        )}
      </AppModalContent>
    </AppModal>
  );
}
